import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { 
  Key, Plus, Search, Copy, RefreshCw, Ban, CheckCircle, 
  MoreHorizontal, CreditCard, Users, TrendingUp, AlertCircle, Calendar, Lock, LogOut, Shield, Monitor, Eye, Trash2, MinusCircle, Upload, Package, Unplug,
  Activity, XCircle, CheckCircle2, Image as ImageIcon, ExternalLink, DollarSign, Layers
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Storage key - namespaced to avoid conflicts with client portal
const ADMIN_TOKEN_KEY = "admin_auth_token";

export default function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminToken, setAdminToken] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  
  const [licenses, setLicenses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [productFilter, setProductFilter] = useState("all");
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showReduceModal, setShowReduceModal] = useState(false);
  const [showIncreaseLimitModal, setShowIncreaseLimitModal] = useState(false);
  const [showDecreaseLimitModal, setShowDecreaseLimitModal] = useState(false);
  const [showAccountsModal, setShowAccountsModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [reassignToProductId, setReassignToProductId] = useState("");
  const [reassignSaving, setReassignSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [productModalMode, setProductModalMode] = useState("create"); // "create" | "edit"
  const [productForm, setProductForm] = useState(null);
  const [productSaving, setProductSaving] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [statsProduct, setStatsProduct] = useState(null);
  const [productStats, setProductStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [tvRequests, setTvRequests] = useState([]);
  const [tvLoading, setTvLoading] = useState(false);
  const [tvRowLoading, setTvRowLoading] = useState(null);
  const [selectedLicenseIds, setSelectedLicenseIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showBulkExtendModal, setShowBulkExtendModal] = useState(false);
  const [bulkExtendMode, setBulkExtendMode] = useState("extend"); // "extend" | "reduce"
  const [bulkExtendDays, setBulkExtendDays] = useState(30);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(null); // account_number being disconnected
  
  const [createEmail, setCreateEmail] = useState("");
  const [createEmailLocked, setCreateEmailLocked] = useState(false);
  const [createDays, setCreateDays] = useState(30);
  const [createProductId, setCreateProductId] = useState("monk-breakout-ea");
  const [createMt5Qty, setCreateMt5Qty] = useState(2);
  const [createNtQty, setCreateNtQty] = useState(0);
  const [extendDays, setExtendDays] = useState(30);
  const [extendMt5, setExtendMt5] = useState(true);
  const [extendNt, setExtendNt] = useState(true);
  const [reduceDays, setReduceDays] = useState(7);
  const [reduceMt5, setReduceMt5] = useState(true);
  const [reduceNt, setReduceNt] = useState(true);
  const [addMt5Qty, setAddMt5Qty] = useState(0);
  const [addNtQty, setAddNtQty] = useState(0);
  const [increaseLimitLoading, setIncreaseLimitLoading] = useState(false);
  const [removeMt5Qty, setRemoveMt5Qty] = useState(0);
  const [removeNtQty, setRemoveNtQty] = useState(0);
  const [decreaseLimitLoading, setDecreaseLimitLoading] = useState(false);
  
  // EA download settings state (Google Drive-based)
  const [uploadVersion, setUploadVersion] = useState("");
  const [uploadFilename, setUploadFilename] = useState("");
  const [uploadDriveUrl, setUploadDriveUrl] = useState("");
  const [uploadLoading, setUploadLoading] = useState(false);
  const [currentEaInfo, setCurrentEaInfo] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    const savedToken = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (savedToken) {
      verifySession(savedToken);
    }
  }, []);

  const verifySession = async (token) => {
    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { "X-Admin-Token": token }
      });
      setAdminToken(token);
      setIsAuthenticated(true);
    } catch (error) {
      // Silently clear invalid token - don't show error during initial load
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      setAdminToken("");
      setIsAuthenticated(false);
      // Don't show toast here - user will just see login screen
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginPassword) {
      toast.error("Please enter admin password");
      return;
    }
    
    setLoginLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, {
        password: loginPassword
      });
      
      if (response.data.success) {
        const token = response.data.token;
        setAdminToken(token);
        localStorage.setItem(ADMIN_TOKEN_KEY, token);
        setIsAuthenticated(true);
        setLoginPassword("");
        toast.success("Admin login successful");
      }
    } catch (error) {
      toast.error("Invalid admin password");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, {
        headers: { "X-Admin-Token": adminToken }
      });
    } catch (error) {
      // Ignore logout errors
    }
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setAdminToken("");
    setIsAuthenticated(false);
  };
  
  // Silent logout - used when session expires during data fetch
  const silentLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setAdminToken("");
    setIsAuthenticated(false);
  };

  const fetchData = useCallback(async () => {
    if (!adminToken) return;
    
    // Create headers inside the function to ensure fresh token
    const headers = { "X-Admin-Token": adminToken };
    
    setLoading(true);
    try {
      const [licensesRes, paymentsRes, statsRes, productsRes] = await Promise.all([
        axios.get(`${API}/admin/license-data`, { headers }),
        axios.get(`${API}/admin/payments`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/products`, { headers })
      ]);
      setLicenses(licensesRes.data);
      setPayments(paymentsRes.data);
      setStats(statsRes.data);
      setProducts(productsRes.data?.products || []);
    } catch (error) {
      if (error.response?.status === 401) {
        // Session expired - silently log out and show login screen
        silentLogout();
      } else {
        toast.error("Failed to fetch data");
      }
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    if (isAuthenticated && adminToken) {
      fetchData();
    }
  }, [isAuthenticated, adminToken, fetchData]);

  // Headers for API calls (used by action functions)
  const apiHeaders = { "X-Admin-Token": adminToken };

  // Feb 2026 — "Add Product" flow: opens the Create License modal with the
  // client's email pre-filled and locked. Everything else (product, quantities,
  // duration) is exactly the same as creating a fresh license — the backend
  // already keys on (email, product_id) so a NEW row is inserted rather than
  // overwriting the existing license.
  const openAddProductForClient = (license) => {
    setCreateEmail(license.email);
    setCreateEmailLocked(true);
    // Default to a different product than the one they already have to
    // reduce the chance of accidentally trying to overwrite.
    const otherProduct = products.find(
      (p) => p.active && p.product_id !== license.product_id
    );
    if (otherProduct) {
      setCreateProductId(otherProduct.product_id);
      setCreateDays(otherProduct.cycle_days || 30);
    }
    setCreateMt5Qty(2);
    setCreateNtQty(0);
    setShowCreateModal(true);
  };

  const createLicense = async () => {
    if (!createEmail || !createEmail.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    const selectedProduct = products.find((p) => p.product_id === createProductId);
    const isOrbFamily = (selectedProduct?.display_group || "") === "monk-algo-orb";
    const supportsNt = !!selectedProduct?.ninjatrader_enabled;
    const mt5Q = Math.max(0, parseInt(createMt5Qty) || 0);
    const ntQ = supportsNt ? Math.max(0, parseInt(createNtQty) || 0) : 0;
    const totalQ = mt5Q + ntQ;
    if (isOrbFamily && totalQ < 2) {
      toast.error("ORB licenses require a minimum of 2 total activations (MT5 + NinjaTrader).");
      return;
    }
    if (totalQ < 1) {
      toast.error("Total activations must be at least 1.");
      return;
    }
    try {
      await axios.post(`${API}/admin/licenses`, {
        email: createEmail,
        duration_days: createDays,
        product_id: createProductId,
        mt5_quantity: mt5Q,
        ninjatrader_quantity: ntQ,
      }, { headers: apiHeaders });
      toast.success(`License created for ${createEmail}`);
      setShowCreateModal(false);
      setCreateEmail("");
      setCreateEmailLocked(false);
      setCreateDays(30);
      setCreateProductId("monk-breakout-ea");
      setCreateMt5Qty(2);
      setCreateNtQty(0);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create license");
    }
  };

  /* ---------- Product CRUD ---------- */
  const EMPTY_PRODUCT_FORM = {
    product_id: "",
    name: "",
    display_group: "",
    billing_cycle: "monthly",
    price_paise: 999000,
    drive_url: "",
    razorpay_link: "",
    tv_enabled: false,
    license_enabled: true,
    active: true,
    version: "",
    image_url: "",
    banner_url: "",
    logo_url: "",
    // NinjaTrader entitlement (Feb 2026) — only accepted on the
    // 'monk-algo-orb' display_group. Backend rejects any other group.
    ninjatrader_enabled: false,
    nt_drive_url: "",
    nt_max_devices: 2,
    nt_version: "",
  };

  const openCreateProductModal = () => {
    setProductForm({ ...EMPTY_PRODUCT_FORM });
    setProductModalMode("create");
    setShowProductModal(true);
  };

  const openEditProductModal = (product) => {
    setProductForm({
      product_id: product.product_id || "",
      name: product.name || "",
      display_group: product.display_group || product.product_id || "",
      billing_cycle: product.billing_cycle || "monthly",
      price_paise: product.price_paise || 0,
      drive_url: product.drive_url || "",
      razorpay_link: product.razorpay_link || "",
      tv_enabled: !!product.tv_enabled,
      license_enabled: product.license_enabled !== false,
      active: product.active !== false,
      version: product.version || "",
      image_url: product.image_url || "",
      banner_url: product.banner_url || "",
      logo_url: product.logo_url || "",
      ninjatrader_enabled: !!product.ninjatrader_enabled,
      nt_drive_url: product.nt_drive_url || "",
      nt_max_devices: product.nt_max_devices || 2,
      nt_version: product.nt_version || "",
    });
    setProductModalMode("edit");
    setShowProductModal(true);
  };

  const saveProduct = async () => {
    if (!productForm) return;
    // Basic client-side validation
    if (!productForm.product_id.trim()) {
      toast.error("Product Slug (product_id) is required");
      return;
    }
    if (!productForm.name.trim()) {
      toast.error("Product Name is required");
      return;
    }
    if (!["monthly", "quarterly", "yearly"].includes(productForm.billing_cycle)) {
      toast.error("Billing cycle must be monthly / quarterly / yearly");
      return;
    }
    if (!productForm.price_paise || productForm.price_paise <= 0) {
      toast.error("Price (paise) must be greater than 0");
      return;
    }
    const payload = {
      ...productForm,
      product_id: productForm.product_id.trim().toLowerCase(),
      display_group: (productForm.display_group || productForm.product_id).trim().toLowerCase(),
      price_paise: Number(productForm.price_paise),
    };
    setProductSaving(true);
    try {
      if (productModalMode === "create") {
        await axios.post(`${API}/admin/products`, payload, { headers: apiHeaders });
        toast.success(`Product "${payload.name}" created`);
      } else {
        // product_id is immutable on update
        const { product_id, ...updates } = payload;
        await axios.put(`${API}/admin/products/${encodeURIComponent(product_id)}`, updates, {
          headers: apiHeaders,
        });
        toast.success(`Product "${payload.name}" updated`);
      }
      setShowProductModal(false);
      setProductForm(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save product");
    } finally {
      setProductSaving(false);
    }
  };

  const deleteProduct = async (product) => {
    const confirmed = window.confirm(
      `Deactivate product "${product.name}"?\n\n` +
      `The product will be hidden from client portals immediately, but existing licenses remain untouched. This is a soft delete (active=false).`
    );
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/products/${encodeURIComponent(product.product_id)}`, {
        headers: apiHeaders,
      });
      toast.success(`Product "${product.name}" deactivated`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deactivate product");
    }
  };

  const openReassignModal = (license) => {
    setReassignTarget(license);
    setReassignToProductId(license.product_id || "monk-breakout-ea");
    setShowReassignModal(true);
  };

  const adminCancelSubscription = async (license) => {
    const productName = productMap[license.product_id || "monk-breakout-ea"]?.name || license.product_id;
    if (!window.confirm(
      `Cancel Razorpay auto-renewal for ${license.email} on ${productName}?\n\n` +
      `The license will stay active until ${license.expiry_date?.slice(0, 10)}. No further charges will be made. The customer will still be able to renew manually later.`
    )) return;
    try {
      const res = await axios.post(
        `${API}/admin/licenses/${license.id}/cancel-subscription`,
        {},
        { headers: apiHeaders }
      );
      if (res.data?.already_cancelled) {
        toast.info("Subscription was already cancelled");
      } else {
        toast.success("Auto-renewal cancelled at Razorpay");
      }
      fetchData();
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 402) {
        toast.error(typeof detail === "string" ? detail : "No active Razorpay subscription on this license");
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to cancel subscription");
      }
    }
  };

  const submitReassign = async () => {
    if (!reassignTarget || !reassignToProductId) return;
    if (reassignToProductId === (reassignTarget.product_id || "monk-breakout-ea")) {
      toast.info("License is already tagged to that product");
      setShowReassignModal(false);
      return;
    }
    setReassignSaving(true);
    try {
      await axios.put(
        `${API}/admin/licenses/${reassignTarget.id}/product`,
        { product_id: reassignToProductId },
        { headers: apiHeaders }
      );
      toast.success(`License retagged to ${products.find(p => p.product_id === reassignToProductId)?.name || reassignToProductId}`);
      setShowReassignModal(false);
      setReassignTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to retag license");
    } finally {
      setReassignSaving(false);
    }
  };

  /* ---------- Product stats ---------- */
  const openProductStats = async (product) => {
    setStatsProduct(product);
    setShowStatsModal(true);
    setProductStats(null);
    setStatsLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/products/${encodeURIComponent(product.product_id)}/stats`,
        { headers: apiHeaders }
      );
      setProductStats(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load stats");
    } finally {
      setStatsLoading(false);
    }
  };

  /* ---------- TradingView requests ---------- */
  const fetchTvRequests = async () => {
    setTvLoading(true);
    try {
      const res = await axios.get(`${API}/admin/tradingview-requests`, { headers: apiHeaders });
      setTvRequests(res.data?.requests || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load TV requests");
    } finally {
      setTvLoading(false);
    }
  };

  const setTvStatus = async (license_id, status) => {
    setTvRowLoading(license_id);
    try {
      await axios.put(
        `${API}/admin/licenses/${license_id}/tv-status`,
        { status },
        { headers: apiHeaders }
      );
      toast.success(status === "granted" ? "Access granted" : "Reset to pending");
      // Update local rows
      setTvRequests((prev) =>
        prev.map((r) =>
          r.license_id === license_id
            ? { ...r, tv_access_status: status, granted_at: status === "granted" ? new Date().toISOString() : null }
            : r
        )
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update TV status");
    } finally {
      setTvRowLoading(null);
    }
  };

  /* ---------- Bulk license actions ---------- */
  const toggleLicenseSelect = (id) => {
    setSelectedLicenseIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllVisible = () => {
    const visibleIds = filteredLicenses.map((l) => l.id);
    const allSelected = visibleIds.every((id) => selectedLicenseIds.includes(id));
    if (allSelected) {
      setSelectedLicenseIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedLicenseIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const clearBulkSelection = () => setSelectedLicenseIds([]);

  const runBulkAction = async (action, days = null) => {
    if (selectedLicenseIds.length === 0) return;
    const humanAction = {
      extend: `extend by ${days} days`,
      reduce: `reduce by ${days} days`,
      deactivate: "DEACTIVATE",
      activate: "activate",
      disconnect_all: "disconnect ALL MT5 accounts from",
    }[action];
    if (!window.confirm(`This will ${humanAction} ${selectedLicenseIds.length} license(s). Continue?`)) return;

    setBulkLoading(true);
    try {
      const body = { action, license_ids: selectedLicenseIds };
      if (days) body.days = days;
      const res = await axios.post(`${API}/admin/licenses/bulk`, body, { headers: apiHeaders });
      const { succeeded, failed_count } = res.data || {};
      if (failed_count > 0) {
        toast.warning(`${succeeded} succeeded, ${failed_count} failed. Check console for details.`);
        console.warn("Bulk failures:", res.data.failed);
      } else {
        toast.success(`${succeeded} license(s) updated`);
      }
      clearBulkSelection();
      setShowBulkExtendModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk action failed");
    } finally {
      setBulkLoading(false);
    }
  };

  const extendLicense = async () => {
    if (!selectedLicense) return;
    if (!extendMt5 && !extendNt) {
      toast.error("Select at least one platform to extend.");
      return;
    }
    try {
      await axios.put(`${API}/admin/licenses/${selectedLicense.id}/extend`, {
        duration_days: extendDays,
        extend_mt5: extendMt5,
        extend_nt: extendNt,
      }, { headers: apiHeaders });
      const parts = [];
      if (extendMt5) parts.push("MT5");
      if (extendNt) parts.push("NinjaTrader");
      toast.success(`Extended ${parts.join(" + ")} by ${extendDays} day(s)`);
      setShowExtendModal(false);
      setSelectedLicense(null);
      setExtendDays(30);
      setExtendMt5(true);
      setExtendNt(true);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to extend license");
    }
  };

  const reduceLicense = async () => {
    if (!selectedLicense) return;
    if (!reduceMt5 && !reduceNt) {
      toast.error("Select at least one platform to reduce.");
      return;
    }
    try {
      await axios.put(`${API}/admin/licenses/${selectedLicense.id}/reduce`, {
        duration_days: reduceDays,
        extend_mt5: reduceMt5,
        extend_nt: reduceNt,
      }, { headers: apiHeaders });
      const parts = [];
      if (reduceMt5) parts.push("MT5");
      if (reduceNt) parts.push("NinjaTrader");
      toast.success(`Reduced ${parts.join(" + ")} by ${reduceDays} day(s)`);
      setShowReduceModal(false);
      setSelectedLicense(null);
      setReduceDays(7);
      setReduceMt5(true);
      setReduceNt(true);
      fetchData();
    } catch (error) {
      console.error("Reduce error:", error.response?.data || error.message);
      toast.error(error.response?.data?.detail || "Failed to reduce license expiry");
    }
  };

  // Feb 2026 — Increase License Limit: adds MT5 / NT seats to an existing
  // license WITHOUT touching validity, product, status, or registered
  // accounts. Fully independent of Add Product / Extend / Reduce.
  const openIncreaseLimitModal = (license) => {
    setSelectedLicense(license);
    setAddMt5Qty(0);
    setAddNtQty(0);
    setShowIncreaseLimitModal(true);
  };

  const increaseLicenseLimit = async () => {
    if (!selectedLicense) return;
    const addMt5 = Math.max(0, parseInt(addMt5Qty) || 0);
    const addNt = Math.max(0, parseInt(addNtQty) || 0);
    if (addMt5 === 0 && addNt === 0) {
      toast.error("Add at least one MT5 or NinjaTrader seat.");
      return;
    }
    const curMt5 = typeof selectedLicense.mt5_max_activations === "number"
      ? selectedLicense.mt5_max_activations : 2;
    const curNt = typeof selectedLicense.nt_max_activations === "number"
      ? selectedLicense.nt_max_activations : 0;
    const newMt5 = curMt5 + addMt5;
    const newNt = curNt + addNt;
    // Confirmation before applying
    if (!window.confirm(
      `Increase access from MT5 ${curMt5} → ${newMt5}` +
      (addNt > 0 ? ` and NT ${curNt} → ${newNt}` : "") +
      `?\n\nExpiry, product, status, and registered accounts will NOT change.`
    )) return;
    setIncreaseLimitLoading(true);
    try {
      await axios.put(`${API}/admin/licenses/${selectedLicense.id}/increase-limit`, {
        additional_mt5: addMt5,
        additional_nt: addNt,
      }, { headers: apiHeaders });
      const parts = [];
      if (addMt5 > 0) parts.push(`+${addMt5} MT5`);
      if (addNt > 0) parts.push(`+${addNt} NT`);
      toast.success(`Increased license limit (${parts.join(", ")})`);
      setShowIncreaseLimitModal(false);
      setSelectedLicense(null);
      setAddMt5Qty(0);
      setAddNtQty(0);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to increase license limit");
    } finally {
      setIncreaseLimitLoading(false);
    }
  };

  // Feb 2026 — Decrease License Limit: mirror of increaseLicenseLimit.
  // Never allows the new cap to go below already-registered accounts/devices.
  const openDecreaseLimitModal = (license) => {
    setSelectedLicense(license);
    setRemoveMt5Qty(0);
    setRemoveNtQty(0);
    setShowDecreaseLimitModal(true);
  };

  const decreaseLicenseLimit = async () => {
    if (!selectedLicense) return;
    const remMt5 = Math.max(0, parseInt(removeMt5Qty) || 0);
    const remNt = Math.max(0, parseInt(removeNtQty) || 0);
    if (remMt5 === 0 && remNt === 0) {
      toast.error("Remove at least one MT5 or NinjaTrader seat.");
      return;
    }
    const curMt5 = typeof selectedLicense.mt5_max_activations === "number"
      ? selectedLicense.mt5_max_activations : 2;
    const curNt = typeof selectedLicense.nt_max_activations === "number"
      ? selectedLicense.nt_max_activations : 0;
    const newMt5 = curMt5 - remMt5;
    const newNt = curNt - remNt;
    const registeredMt5 = (selectedLicense.registered_accounts || []).length;
    const registeredNt = (selectedLicense.nt_activations || []).length;
    // Client-side guard (backend re-checks authoritatively).
    if (newMt5 < 0 || newNt < 0) {
      toast.error("New limit can't go below 0.");
      return;
    }
    if (newMt5 < registeredMt5) {
      toast.error(`Can't decrease MT5 below ${registeredMt5} — that's how many accounts are already registered.`);
      return;
    }
    if (newNt < registeredNt) {
      toast.error(`Can't decrease NinjaTrader below ${registeredNt} — that's how many devices are already registered.`);
      return;
    }
    if (!window.confirm(
      `Decrease access from MT5 ${curMt5} → ${newMt5}` +
      (remNt > 0 ? ` and NT ${curNt} → ${newNt}` : "") +
      `?\n\nExpiry, product, status, and registered accounts will NOT change.`
    )) return;
    setDecreaseLimitLoading(true);
    try {
      await axios.put(`${API}/admin/licenses/${selectedLicense.id}/decrease-limit`, {
        remove_mt5: remMt5,
        remove_nt: remNt,
      }, { headers: apiHeaders });
      const parts = [];
      if (remMt5 > 0) parts.push(`-${remMt5} MT5`);
      if (remNt > 0) parts.push(`-${remNt} NT`);
      toast.success(`Decreased license limit (${parts.join(", ")})`);
      setShowDecreaseLimitModal(false);
      setSelectedLicense(null);
      setRemoveMt5Qty(0);
      setRemoveNtQty(0);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to decrease license limit");
    } finally {
      setDecreaseLimitLoading(false);
    }
  };

  const deactivateLicense = async (license) => {
    try {
      await axios.put(`${API}/admin/licenses/${license.id}/deactivate`, {}, { headers: apiHeaders });
      toast.success("License deactivated");
      fetchData();
    } catch (error) {
      toast.error("Failed to deactivate");
    }
  };

  const activateLicense = async (license) => {
    try {
      await axios.put(`${API}/admin/licenses/${license.id}/activate`, {}, { headers: apiHeaders });
      toast.success("License activated");
      fetchData();
    } catch (error) {
      toast.error("Failed to activate");
    }
  };

  const deleteLicense = async () => {
    if (!selectedLicense) return;
    
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/admin/licenses/${selectedLicense.id}`, { headers: apiHeaders });
      toast.success("License permanently deleted");
      setShowDeleteModal(false);
      setSelectedLicense(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete license");
    } finally {
      setDeleteLoading(false);
    }
  };

  const openDeleteModal = (license) => {
    setSelectedLicense(license);
    setShowDeleteModal(true);
  };

  const disconnectAccount = async (license, accountNumber) => {
    if (!license || !accountNumber) return;
    const confirmed = window.confirm(
      `Disconnect account ${accountNumber} from ${license.email}?\n\n` +
      `The EA will stop working on this account and the slot will become available again.`
    );
    if (!confirmed) return;
    setDisconnectLoading(accountNumber);
    try {
      const res = await axios.delete(
        `${API}/admin/licenses/${license.id}/accounts/${encodeURIComponent(accountNumber)}`,
        { headers: apiHeaders }
      );
      toast.success(res.data?.message || "Account disconnected");
      // Update the modal view in-place so the user sees the change instantly
      setSelectedLicense((prev) =>
        prev && prev.id === license.id
          ? { ...prev, registered_accounts: res.data?.registered_accounts || [] }
          : prev
      );
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to disconnect account");
    } finally {
      setDisconnectLoading(null);
    }
  };

  const disconnectNtDevice = async (license, activation) => {
    if (!license || !activation) return;
    const accountNumber = String(activation.account_number || "").trim();
    const deviceId = activation.device_id || "";
    // Prefer account-scoped disconnect (Feb 2026 canonical key). Fall back
    // to the legacy device endpoint only when the row predates the switch
    // and has no account_number attached.
    const isLegacy = !accountNumber;
    const label = accountNumber || deviceId;
    const confirmed = window.confirm(
      isLegacy
        ? `Disconnect legacy NinjaTrader device ${deviceId} from ${license.email}?\n\n` +
          `The NT strategy on that machine will be refused on its next heartbeat and the slot becomes available again.`
        : `Disconnect NinjaTrader account ${accountNumber} from ${license.email}?\n\n` +
          `Only this account will be disconnected. Other accounts on the same machine remain active.`
    );
    if (!confirmed) return;
    setDisconnectLoading(`nt-${label}`);
    try {
      const url = isLegacy
        ? `${API}/admin/licenses/${license.id}/nt-devices/${encodeURIComponent(deviceId)}`
        : `${API}/admin/licenses/${license.id}/nt-accounts/${encodeURIComponent(accountNumber)}`;
      const res = await axios.delete(url, { headers: apiHeaders });
      toast.success(res.data?.message || "NinjaTrader account disconnected");
      setSelectedLicense((prev) =>
        prev && prev.id === license.id
          ? { ...prev, nt_activations: res.data?.nt_activations || [] }
          : prev
      );
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to disconnect NinjaTrader account");
    } finally {
      setDisconnectLoading(null);
    }
  };

  // EA download settings (Google Drive URL-based)
  const fetchEaInfo = async () => {
    try {
      const response = await axios.get(`${API}/admin/ea-config`, { headers: apiHeaders });
      setCurrentEaInfo(response.data);
    } catch (error) {
      console.error("Failed to fetch EA config:", error);
    }
  };

  const handleUploadEA = async () => {
    if (!uploadDriveUrl.trim()) {
      toast.error("Please paste the Google Drive download URL");
      return;
    }
    if (!uploadDriveUrl.startsWith("http://") && !uploadDriveUrl.startsWith("https://")) {
      toast.error("URL must start with https://");
      return;
    }
    if (uploadFilename && !uploadFilename.toLowerCase().endsWith(".ex5")) {
      toast.error("Filename must end with .ex5");
      return;
    }

    setUploadLoading(true);
    try {
      const body = { download_url: uploadDriveUrl.trim() };
      if (uploadVersion.trim()) body.version = uploadVersion.trim();
      if (uploadFilename.trim()) body.filename = uploadFilename.trim();

      const response = await axios.put(
        `${API}/admin/ea-config`,
        body,
        { headers: { ...apiHeaders, "Content-Type": "application/json" } }
      );

      toast.success(`EA download link updated (version ${response.data.version})`);
      setShowUploadModal(false);
      setUploadDriveUrl("");
      setUploadFilename("");
      setUploadVersion("");
      fetchEaInfo();
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.response?.data?.detail || "Failed to update EA settings");
    } finally {
      setUploadLoading(false);
    }
  };

  const openUploadModal = async () => {
    // Fetch current config and pre-fill the form so admin can edit
    try {
      const response = await axios.get(`${API}/admin/ea-config`, { headers: apiHeaders });
      setCurrentEaInfo(response.data);
      setUploadDriveUrl(response.data.download_url || "");
      setUploadFilename(response.data.filename || "");
      setUploadVersion(response.data.version || "");
    } catch (error) {
      console.error("Failed to fetch EA config:", error);
    }
    setShowUploadModal(true);
  };

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      toast.success("Copied");
    } catch (err) {
      toast.error("Copy failed");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: "bg-green-100 text-green-800 border-green-300",
      expired: "bg-gray-100 text-gray-700 border-gray-300",
      deactivated: "bg-red-100 text-red-800 border-red-300",
      paid: "bg-green-100 text-green-800 border-green-300",
      created: "bg-yellow-100 text-yellow-800 border-yellow-300"
    };
    return (
      <Badge variant="outline" className={`${styles[status] || styles.expired} font-medium text-xs`}>
        {status?.toUpperCase()}
      </Badge>
    );
  };

  const getDaysRemaining = (expiryDate) => {
    const expiry = new Date(expiryDate);
    const now = new Date();
    return Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // Map product_id -> product doc for quick lookups
  const productMap = products.reduce((acc, p) => {
    acc[p.product_id] = p;
    return acc;
  }, {});

  const filteredLicenses = licenses.filter(license => {
    const matchesSearch = license.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         license.license_key.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || license.status === statusFilter;
    const licProduct = license.product_id || "monk-breakout-ea";
    const matchesProduct = productFilter === "all" || licProduct === productFilter;
    return matchesSearch && matchesStatus && matchesProduct;
  });

  const filteredPayments = payments.filter(payment => {
    return payment.email.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Admin Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-gray-800 border border-gray-700 shadow-2xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-indigo-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-9 h-9 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-white">Admin Panel</CardTitle>
            <CardDescription className="text-gray-400">
              Enter admin password to access
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-300">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••••••"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="pl-10 bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                    data-testid="admin-password-input"
                  />
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                disabled={loginLoading}
                data-testid="admin-login-btn"
              >
                {loginLoading ? "Authenticating..." : "Login to Admin"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
              <Key className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
              <p className="text-xs text-gray-500">License Management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => setShowCreateModal(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="create-license-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create License
            </Button>
            <Button 
              onClick={openUploadModal}
              variant="outline"
              className="border-green-500 text-green-600 hover:bg-green-50"
              data-testid="upload-ea-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              EA Link
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => { handleLogout(); toast.success("Logged out"); }}
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <Card className="bg-white border border-gray-200">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-xs font-medium text-gray-500 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Total Licenses
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-2xl font-bold text-gray-900">{stats?.licenses?.total || 0}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white border border-gray-200">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-xs font-medium text-gray-500 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Active
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-2xl font-bold text-green-600">{stats?.licenses?.active || 0}</p>
            </CardContent>
          </Card>

          <Card className="bg-white border border-gray-200">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-xs font-medium text-gray-500 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-gray-500" />
                Expired
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-2xl font-bold text-gray-600">{stats?.licenses?.expired || 0}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white border border-gray-200">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-xs font-medium text-gray-500 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Payments
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-2xl font-bold text-gray-900">{stats?.payments?.successful || 0}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-white border border-gray-200">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-xs font-medium text-gray-500 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-indigo-600" />
                Revenue
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-2xl font-bold text-indigo-600">₹{(stats?.payments?.revenue || 0).toLocaleString()}</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Card className="bg-white border border-gray-200">
          <Tabs
            defaultValue="licenses"
            className="w-full"
            onValueChange={(v) => {
              if (v === "tvaccess" && tvRequests.length === 0) fetchTvRequests();
            }}
          >
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-4 border-b border-gray-200">
              <TabsList className="bg-gray-100">
                <TabsTrigger value="licenses" data-testid="licenses-tab">Licenses</TabsTrigger>
                <TabsTrigger value="payments" data-testid="payments-tab">Payments</TabsTrigger>
                <TabsTrigger value="products" data-testid="products-tab">Products</TabsTrigger>
                <TabsTrigger value="tvaccess" data-testid="tvaccess-tab">TV Access</TabsTrigger>
              </TabsList>
              
              <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                <div className="relative flex-1 sm:flex-none">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 w-full sm:w-56 bg-white"
                    data-testid="search-input"
                  />
                </div>
                <Select value={productFilter} onValueChange={setProductFilter}>
                  <SelectTrigger className="w-40 bg-white" data-testid="product-filter">
                    <SelectValue placeholder="Product" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Products</SelectItem>
                    {products.map((p) => (
                      <SelectItem key={p.product_id} value={p.product_id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-32 bg-white" data-testid="status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="deactivated">Deactivated</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={fetchData} data-testid="refresh-btn">
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>

            <TabsContent value="licenses" className="m-0">
              {selectedLicenseIds.length > 0 && (
                <div className="p-3 bg-indigo-50 border-b border-indigo-200 flex flex-wrap items-center gap-2" data-testid="bulk-actions-bar">
                  <span className="text-sm font-medium text-indigo-900">
                    {selectedLicenseIds.length} selected
                  </span>
                  <div className="flex-1" />
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={bulkLoading}
                    onClick={() => { setBulkExtendMode("extend"); setBulkExtendDays(30); setShowBulkExtendModal(true); }}
                    data-testid="bulk-extend-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Extend
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={bulkLoading}
                    onClick={() => { setBulkExtendMode("reduce"); setBulkExtendDays(7); setShowBulkExtendModal(true); }}
                    data-testid="bulk-reduce-btn"
                  >
                    <MinusCircle className="w-4 h-4 mr-1" /> Reduce
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={bulkLoading}
                    onClick={() => runBulkAction("activate")}
                    className="border-green-300 text-green-700 hover:bg-green-50"
                    data-testid="bulk-activate-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" /> Activate
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={bulkLoading}
                    onClick={() => runBulkAction("deactivate")}
                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                    data-testid="bulk-deactivate-btn"
                  >
                    <Ban className="w-4 h-4 mr-1" /> Deactivate
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={bulkLoading}
                    onClick={() => runBulkAction("disconnect_all")}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    data-testid="bulk-disconnect-btn"
                  >
                    <Unplug className="w-4 h-4 mr-1" /> Disconnect Accts
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={clearBulkSelection}
                    disabled={bulkLoading}
                    data-testid="bulk-clear-btn"
                  >
                    Clear
                  </Button>
                </div>
              )}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-8">
                        <input
                          type="checkbox"
                          checked={filteredLicenses.length > 0 && filteredLicenses.every((l) => selectedLicenseIds.includes(l.id))}
                          onChange={toggleSelectAllVisible}
                          data-testid="bulk-select-all"
                          aria-label="Select all visible"
                        />
                      </TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">License Key</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Email</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Product</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Status</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Trading Accounts</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Expiry</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Days Left</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLicenses.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-12 text-gray-500">
                          {loading ? "Loading..." : "No licenses found"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredLicenses.map((license) => {
                        const daysRemaining = getDaysRemaining(license.expiry_date);
                        const accounts = license.registered_accounts || license.accounts || license.trading_accounts || [];
                        const accountCount = accounts.length;
                        const licProductId = license.product_id || "monk-breakout-ea";
                        const licProduct = productMap[licProductId];
                        const isSelected = selectedLicenseIds.includes(license.id);
                        return (
                          <TableRow key={license.id} className={`hover:bg-gray-50 ${isSelected ? "bg-indigo-50/40" : ""}`}>
                            <TableCell>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleLicenseSelect(license.id)}
                                data-testid={`select-license-${license.id}`}
                                aria-label={`Select ${license.email}`}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <code className="font-mono text-xs bg-gray-100 px-2 py-1 rounded border">
                                  {license.license_key}
                                </code>
                                <button 
                                  onClick={() => copyToClipboard(license.license_key)}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  <Copy className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-gray-700">{license.email}</TableCell>
                            <TableCell className="text-sm">
                              <div className="flex flex-col gap-1">
                                <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200 w-fit">
                                  {licProduct?.name || licProductId}
                                </Badge>
                                {license.tv_username && (
                                  <span className="text-xs text-blue-600" title="TradingView username">
                                    TV: {license.tv_username}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>{getStatusBadge(license.status)}</TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <button
                                  onClick={() => { setSelectedLicense({...license, registered_accounts: accounts}); setShowAccountsModal(true); }}
                                  className="flex items-center gap-1.5 text-sm hover:text-indigo-600"
                                >
                                  <Monitor className="w-3.5 h-3.5" />
                                  <span className={accountCount > 0 ? "text-indigo-600 font-medium" : "text-gray-400"}>
                                    {accountCount}/{license.mt5_max_activations || 2}
                                  </span>
                                  {license.nt_activations && license.nt_activations.length > 0 && (
                                    <span
                                      className="ml-1 inline-flex items-center gap-1 text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5"
                                      title={`${license.nt_activations.length} NinjaTrader device(s) connected`}
                                      data-testid={`admin-nt-count-${license.id}`}
                                    >
                                      <Package className="w-3 h-3" />
                                      NT {license.nt_activations.length}
                                    </span>
                                  )}
                                </button>
                                {accountCount > 0 && (
                                  <div className="text-xs space-y-0.5 max-w-[200px]">
                                    {accounts.slice(0, 2).map((acc, idx) => {
                                      const account = typeof acc === 'object' ? acc : { account: acc, broker: 'Unknown' };
                                      return (
                                        <div key={idx} className="flex items-center gap-1 text-gray-500 truncate" title={`${account.account} - ${account.broker}`}>
                                          <span className="font-mono text-gray-700">{account.account}</span>
                                          <span className="text-gray-400">•</span>
                                          <span className="truncate">{account.broker || 'Unknown'}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-gray-700">
                              {new Date(license.expiry_date).toLocaleDateString('en-IN')}
                            </TableCell>
                            <TableCell>
                              {license.status === "deactivated" ? (
                                <span className="text-gray-400 text-sm">—</span>
                              ) : daysRemaining > 7 ? (
                                <span className="text-green-600 font-medium text-sm">{daysRemaining}d</span>
                              ) : daysRemaining > 0 ? (
                                <span className="text-yellow-600 font-medium text-sm">{daysRemaining}d</span>
                              ) : (
                                <span className="text-red-600 font-medium text-sm">Expired</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => { setSelectedLicense(license); setShowCredentialsModal(true); }}>
                                    <Key className="w-4 h-4 mr-2" /> View Credentials
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => { setSelectedLicense(license); setShowAccountsModal(true); }}>
                                    <Monitor className="w-4 h-4 mr-2" /> View Accounts
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => openAddProductForClient(license)}
                                    data-testid={`add-product-${license.id}`}
                                  >
                                    <Plus className="w-4 h-4 mr-2" /> Add Product
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => { setSelectedLicense(license); setShowExtendModal(true); }}>
                                    <RefreshCw className="w-4 h-4 mr-2" /> Extend
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => { setSelectedLicense(license); setShowReduceModal(true); }}>
                                    <MinusCircle className="w-4 h-4 mr-2" /> Reduce Days
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => openIncreaseLimitModal(license)}
                                    data-testid={`increase-limit-${license.id}`}
                                  >
                                    <TrendingUp className="w-4 h-4 mr-2" /> Increase License Limit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => openDecreaseLimitModal(license)}
                                    data-testid={`decrease-limit-${license.id}`}
                                  >
                                    <MinusCircle className="w-4 h-4 mr-2" /> Decrease License Limit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => openReassignModal(license)}
                                    data-testid={`reassign-product-${license.id}`}
                                  >
                                    <Package className="w-4 h-4 mr-2" /> Change Product
                                  </DropdownMenuItem>
                                  {license.razorpay_subscription_id && !license.auto_renewal_cancelled && (
                                    <DropdownMenuItem
                                      onClick={() => adminCancelSubscription(license)}
                                      className="text-orange-700 focus:text-orange-700"
                                      data-testid={`cancel-subscription-${license.id}`}
                                    >
                                      <XCircle className="w-4 h-4 mr-2" /> Cancel Subscription
                                    </DropdownMenuItem>
                                  )}
                                  {license.status === "deactivated" ? (
                                    <DropdownMenuItem onClick={() => activateLicense(license)}>
                                      <CheckCircle className="w-4 h-4 mr-2" /> Activate
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem onClick={() => deactivateLicense(license)} className="text-red-600">
                                      <Ban className="w-4 h-4 mr-2" /> Deactivate
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem onClick={() => copyToClipboard(license.license_key)}>
                                    <Copy className="w-4 h-4 mr-2" /> Copy Key
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => openDeleteModal(license)} 
                                    className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="payments" className="m-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Order ID</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Email</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Plan</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Amount</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Status</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPayments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-12 text-gray-500">
                          {loading ? "Loading..." : "No payments found"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPayments.map((payment) => (
                        <TableRow key={payment.id} className="hover:bg-gray-50">
                          <TableCell>
                            <code className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                              {payment.razorpay_order_id}
                            </code>
                          </TableCell>
                          <TableCell className="text-sm text-gray-700">{payment.email}</TableCell>
                          <TableCell className="text-sm text-gray-700">{payment.plan_name}</TableCell>
                          <TableCell className="text-sm font-medium text-gray-900">
                            ₹{(payment.amount / 100).toLocaleString()}
                          </TableCell>
                          <TableCell>{getStatusBadge(payment.status)}</TableCell>
                          <TableCell className="text-xs text-gray-500">{formatDate(payment.created_at)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="products" className="m-0">
              <div className="p-4 flex items-center justify-between border-b border-gray-100">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Product Catalog</h3>
                  <p className="text-xs text-gray-500">
                    Master configuration. Fields drive the Client Portal, webhook amount checks and downloads.
                  </p>
                </div>
                <Button
                  onClick={openCreateProductModal}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="create-product-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Product
                </Button>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Name</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Slug</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Group</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Cycle</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Price</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">TV</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">MT5</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Active</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Version</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-12 text-gray-500">
                          {loading ? "Loading..." : "No products yet. Click \"Add Product\" to create one."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      products.map((p) => (
                        <TableRow key={p.product_id} className="hover:bg-gray-50" data-testid={`product-row-${p.product_id}`}>
                          <TableCell className="text-sm font-medium text-gray-900">{p.name}</TableCell>
                          <TableCell>
                            <code className="font-mono text-xs bg-gray-100 px-2 py-1 rounded border">{p.product_id}</code>
                          </TableCell>
                          <TableCell className="text-xs text-gray-500 font-mono">{p.display_group}</TableCell>
                          <TableCell className="text-sm text-gray-700 capitalize">
                            {p.billing_cycle} <span className="text-gray-400">({p.cycle_days}d)</span>
                          </TableCell>
                          <TableCell className="text-sm font-medium text-gray-900">
                            ₹{(p.price_paise / 100).toLocaleString("en-IN")}
                          </TableCell>
                          <TableCell>
                            {p.tv_enabled ? (
                              <Badge className="bg-blue-100 text-blue-800 border-blue-300 text-xs">Yes</Badge>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {p.license_enabled !== false ? (
                              <Badge className="bg-green-100 text-green-800 border-green-300 text-xs">Yes</Badge>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {p.active ? (
                              <Badge className="bg-green-100 text-green-800 border-green-300 text-xs">Active</Badge>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-700 border-gray-300 text-xs">Hidden</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-gray-600">{p.version || "—"}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openProductStats(p)}
                                data-testid={`stats-product-${p.product_id}`}
                                title="View product analytics"
                              >
                                <Activity className="w-4 h-4 mr-1" /> Stats
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditProductModal(p)}
                                data-testid={`edit-product-${p.product_id}`}
                              >
                                <Eye className="w-4 h-4 mr-1" /> Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteProduct(p)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-product-${p.product_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="tvaccess" className="m-0">
              <div className="p-4 flex items-center justify-between border-b border-gray-100">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">TradingView Access Requests</h3>
                  <p className="text-xs text-gray-500">
                    Grant access after inviting the customer&apos;s TradingView username to your invite-only indicator.
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={fetchTvRequests} disabled={tvLoading} data-testid="tv-refresh-btn">
                  <RefreshCw className={`w-4 h-4 mr-2 ${tvLoading ? "animate-spin" : ""}`} /> Refresh
                </Button>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">TV Username</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Product</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Customer</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Submitted</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase">Status</TableHead>
                      <TableHead className="text-xs font-semibold text-gray-600 uppercase text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tvRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-12 text-gray-500">
                          {tvLoading ? "Loading..." : "No TradingView requests yet."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      tvRequests.map((r) => (
                        <TableRow key={r.license_id} data-testid={`tv-row-${r.license_id}`} className="hover:bg-gray-50">
                          <TableCell>
                            <code className="font-mono text-sm font-semibold text-gray-900 bg-gray-100 px-2 py-1 rounded border">
                              {r.tv_username}
                            </code>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">
                              {r.product_name}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-gray-700">{r.email}</TableCell>
                          <TableCell className="text-xs text-gray-500">
                            {r.submitted_at ? new Date(r.submitted_at).toLocaleString("en-IN") : "—"}
                          </TableCell>
                          <TableCell>
                            {r.tv_access_status === "granted" ? (
                              <Badge className="bg-green-100 text-green-800 border-green-300 text-xs">
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Granted
                              </Badge>
                            ) : (
                              <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300 text-xs">Pending</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {r.tv_access_status === "granted" ? (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={tvRowLoading === r.license_id}
                                onClick={() => setTvStatus(r.license_id, "pending")}
                                data-testid={`tv-revoke-${r.license_id}`}
                              >
                                {tvRowLoading === r.license_id ? "..." : "Revoke"}
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                disabled={tvRowLoading === r.license_id}
                                onClick={() => setTvStatus(r.license_id, "granted")}
                                className="bg-green-600 hover:bg-green-700 text-white"
                                data-testid={`tv-grant-${r.license_id}`}
                              >
                                {tvRowLoading === r.license_id ? "..." : (<><CheckCircle className="w-4 h-4 mr-1" /> Grant Access</>)}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </Card>

        {/* API Info */}
        <Card className="mt-6 bg-white border border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-700">Validation API (for EA)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 bg-gray-900 text-gray-100 rounded-md px-4 py-3 font-mono text-sm">
              <span className="text-green-400">GET</span>
              <span className="text-gray-300">/api/licenses/validate?key=LIC-XXXXXXXX-XXXXXXXX</span>
              <button 
                onClick={() => copyToClipboard(`${process.env.REACT_APP_BACKEND_URL}/api/licenses/validate?key=`)}
                className="ml-auto text-gray-400 hover:text-white"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={(open) => {
        setShowCreateModal(open);
        if (!open) {
          // Reset the "add to existing client" lock when the modal closes so
          // the next top-level Create License click starts fresh.
          setCreateEmailLocked(false);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{createEmailLocked ? "Add Product for Client" : "Create License"}</DialogTitle>
            <DialogDescription>
              {createEmailLocked
                ? "Creates a SEPARATE license for this client's existing email. Their current licenses are untouched."
                : "Creates client account and sends credentials via email."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="create-email">Email</Label>
              <Input
                id="create-email"
                type="email"
                placeholder="user@example.com"
                value={createEmail}
                onChange={(e) => setCreateEmail(e.target.value)}
                disabled={createEmailLocked}
                data-testid="create-email-input"
              />
              {createEmailLocked && (
                <p className="text-xs text-indigo-600" data-testid="add-product-locked-email-hint">
                  Adding a new license to this existing client. A new separate license will be created — existing licenses will not be modified.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-product">Product</Label>
              <Select value={createProductId} onValueChange={setCreateProductId}>
                <SelectTrigger data-testid="create-product-select">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {products.filter(p => p.active).map((p) => (
                    <SelectItem key={p.product_id} value={p.product_id}>
                      {p.name} ({p.cycle_days}d, ₹{(p.price_paise / 100).toLocaleString("en-IN")})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Cycle days will default from the product ({products.find(p => p.product_id === createProductId)?.cycle_days || 30}d). Override below if needed.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-days">Duration (days)</Label>
              <Input
                id="create-days"
                type="number"
                min={1}
                value={createDays}
                onChange={(e) => setCreateDays(parseInt(e.target.value) || 30)}
                data-testid="create-days-input"
              />
            </div>

            {/* Feb 2026 — Flexible entitlement quantities */}
            {(() => {
              const selectedProduct = products.find((p) => p.product_id === createProductId);
              const isOrbFamily = (selectedProduct?.display_group || "") === "monk-algo-orb";
              const supportsNt = !!selectedProduct?.ninjatrader_enabled;
              const totalQ = (parseInt(createMt5Qty) || 0) + (supportsNt ? (parseInt(createNtQty) || 0) : 0);
              const belowMin = isOrbFamily && totalQ < 2;
              return (
                <div className="space-y-3 rounded-lg border border-indigo-100 bg-indigo-50/40 p-3" data-testid="create-entitlements-section">
                  <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
                    Purchased Entitlements
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm text-gray-700">MT5 Access</Label>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setCreateMt5Qty((v) => Math.max(0, (parseInt(v) || 0) - 1))}
                        data-testid="create-mt5-decrement"
                      >−</Button>
                      <Input
                        type="number"
                        min={0}
                        value={createMt5Qty}
                        onChange={(e) => setCreateMt5Qty(Math.max(0, parseInt(e.target.value) || 0))}
                        className="h-8 w-16 text-center"
                        data-testid="admin-create-mt5-qty"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setCreateMt5Qty((v) => (parseInt(v) || 0) + 1)}
                        data-testid="create-mt5-increment"
                      >+</Button>
                    </div>
                  </div>
                  {supportsNt && (
                    <div className="flex items-center justify-between">
                      <Label className="text-sm text-gray-700">NinjaTrader Access</Label>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setCreateNtQty((v) => Math.max(0, (parseInt(v) || 0) - 1))}
                          data-testid="create-nt-decrement"
                        >−</Button>
                        <Input
                          type="number"
                          min={0}
                          value={createNtQty}
                          onChange={(e) => setCreateNtQty(Math.max(0, parseInt(e.target.value) || 0))}
                          className="h-8 w-16 text-center"
                          data-testid="admin-create-nt-qty"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setCreateNtQty((v) => (parseInt(v) || 0) + 1)}
                          data-testid="create-nt-increment"
                        >+</Button>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between border-t border-indigo-100 pt-2">
                    <span className="text-sm font-medium text-gray-800">Total Activations</span>
                    <span
                      className={`text-sm font-semibold ${belowMin ? "text-red-600" : "text-indigo-700"}`}
                      data-testid="admin-create-total-qty"
                    >
                      {totalQ}
                    </span>
                  </div>
                  {isOrbFamily && (
                    <p
                      className={`text-xs ${belowMin ? "text-red-600" : "text-gray-500"}`}
                      data-testid="create-orb-min-hint"
                    >
                      ORB requires a minimum of 2 total activations (MT5 + NinjaTrader).
                    </p>
                  )}
                  {!supportsNt && (
                    <p className="text-xs text-gray-500">
                      This product doesn&apos;t support NinjaTrader — NT entitlement is fixed at 0.
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button 
              onClick={createLicense}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="confirm-create-btn"
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend Modal */}
      <Dialog open={showExtendModal} onOpenChange={setShowExtendModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Extend License</DialogTitle>
            <DialogDescription>
              Active: extends from expiry. Expired: extends from today.
            </DialogDescription>
          </DialogHeader>
          {selectedLicense && (() => {
            // Resolve product NT eligibility so we hide the NT checkbox for
            // non-NT products (they can only be "extended" via MT5).
            const lp = products.find((p) => p.product_id === selectedLicense.product_id);
            const supportsNt = !!lp?.ninjatrader_enabled;
            const curMt5 = selectedLicense.mt5_max_activations ?? "—";
            const curNt = selectedLicense.nt_max_activations ?? "—";
            const curTot = selectedLicense.total_purchased_activations
              ?? (typeof selectedLicense.mt5_max_activations === "number"
                  ? (selectedLicense.mt5_max_activations + (selectedLicense.nt_max_activations || 0))
                  : "—");
            const mt5Exp = selectedLicense.mt5_expiry_date || selectedLicense.expiry_date;
            const ntExp = selectedLicense.nt_expiry_date || selectedLicense.expiry_date;
            return (
              <div className="space-y-4 py-4">
                <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Key</span>
                    <code className="font-mono text-xs">{selectedLicense.license_key}</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Email</span>
                    <span>{selectedLicense.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Expiry (latest)</span>
                    <span>{new Date(selectedLicense.expiry_date).toLocaleDateString('en-IN')}</span>
                  </div>
                </div>

                {/* Current Entitlements */}
                <div className="rounded-md border border-indigo-100 bg-indigo-50/40 p-3 space-y-1 text-sm">
                  <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700 mb-1">
                    Current Entitlements
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">MT5</span>
                    <span data-testid="admin-extend-current-mt5">
                      {curMt5}{selectedLicense.mt5_expiry_date ? ` · until ${new Date(mt5Exp).toLocaleDateString('en-IN')}` : ""}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">NinjaTrader</span>
                    <span data-testid="admin-extend-current-nt">
                      {curNt}{selectedLicense.nt_expiry_date ? ` · until ${new Date(ntExp).toLocaleDateString('en-IN')}` : ""}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-indigo-100 pt-1">
                    <span className="text-gray-800 font-medium">Total</span>
                    <span className="font-semibold text-indigo-700" data-testid="admin-extend-current-total">{curTot}</span>
                  </div>
                </div>

                {/* Platform selection */}
                <div className="space-y-2">
                  <Label>Extend Platform Access</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={extendMt5}
                        onChange={(e) => setExtendMt5(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        data-testid="admin-extend-mt5-checkbox"
                      />
                      <span>MT5</span>
                    </label>
                    {supportsNt && (
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={extendNt}
                          onChange={(e) => setExtendNt(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          data-testid="admin-extend-nt-checkbox"
                        />
                        <span>NinjaTrader</span>
                      </label>
                    )}
                  </div>
                  {!extendMt5 && (!supportsNt || !extendNt) && (
                    <p className="text-xs text-red-600">Select at least one platform to extend.</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="extend-days">Extend by (days)</Label>
                  <Input
                    id="extend-days"
                    type="number"
                    min={1}
                    value={extendDays}
                    onChange={(e) => setExtendDays(parseInt(e.target.value) || 30)}
                    data-testid="extend-days-input"
                  />
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExtendModal(false)}>Cancel</Button>
            <Button 
              onClick={extendLicense}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="confirm-extend-btn"
            >
              <Calendar className="w-4 h-4 mr-2" /> Extend
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reduce Days Modal */}
      <Dialog open={showReduceModal} onOpenChange={setShowReduceModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reduce License Expiry</DialogTitle>
            <DialogDescription>
              Reduce the license validity by the specified number of days.
            </DialogDescription>
          </DialogHeader>
          {selectedLicense && (() => {
            const lp = products.find((p) => p.product_id === selectedLicense.product_id);
            const supportsNt = !!lp?.ninjatrader_enabled;
            const curMt5 = selectedLicense.mt5_max_activations ?? "—";
            const curNt = selectedLicense.nt_max_activations ?? "—";
            const curTot = selectedLicense.total_purchased_activations
              ?? (typeof selectedLicense.mt5_max_activations === "number"
                  ? (selectedLicense.mt5_max_activations + (selectedLicense.nt_max_activations || 0))
                  : "—");
            const mt5Exp = selectedLicense.mt5_expiry_date || selectedLicense.expiry_date;
            const ntExp = selectedLicense.nt_expiry_date || selectedLicense.expiry_date;
            return (
              <div className="space-y-4 py-4">
                <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Key</span>
                    <code className="font-mono text-xs">{selectedLicense.license_key}</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Email</span>
                    <span>{selectedLicense.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Current Expiry (latest)</span>
                    <span>{new Date(selectedLicense.expiry_date).toLocaleDateString('en-IN')}</span>
                  </div>
                </div>

                {/* Current Entitlements */}
                <div className="rounded-md border border-orange-100 bg-orange-50/40 p-3 space-y-1 text-sm">
                  <div className="text-xs font-semibold uppercase tracking-wide text-orange-700 mb-1">
                    Current Entitlements
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">MT5</span>
                    <span data-testid="admin-reduce-current-mt5">
                      {curMt5}{selectedLicense.mt5_expiry_date ? ` · until ${new Date(mt5Exp).toLocaleDateString('en-IN')}` : ""}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">NinjaTrader</span>
                    <span data-testid="admin-reduce-current-nt">
                      {curNt}{selectedLicense.nt_expiry_date ? ` · until ${new Date(ntExp).toLocaleDateString('en-IN')}` : ""}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-orange-100 pt-1">
                    <span className="text-gray-800 font-medium">Total</span>
                    <span className="font-semibold text-orange-700" data-testid="admin-reduce-current-total">{curTot}</span>
                  </div>
                </div>

                {/* Platform selection */}
                <div className="space-y-2">
                  <Label>Reduce Platform Access</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={reduceMt5}
                        onChange={(e) => setReduceMt5(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        data-testid="admin-reduce-mt5-checkbox"
                      />
                      <span>MT5</span>
                    </label>
                    {supportsNt && (
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={reduceNt}
                          onChange={(e) => setReduceNt(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                          data-testid="admin-reduce-nt-checkbox"
                        />
                        <span>NinjaTrader</span>
                      </label>
                    )}
                  </div>
                  {!reduceMt5 && (!supportsNt || !reduceNt) && (
                    <p className="text-xs text-red-600">Select at least one platform to reduce.</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reduce-days">Reduce by (days)</Label>
                  <Input
                    id="reduce-days"
                    type="number"
                    min={1}
                    value={reduceDays}
                    onChange={(e) => setReduceDays(parseInt(e.target.value) || 7)}
                    data-testid="reduce-days-input"
                  />
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <p className="text-sm text-yellow-800">
                    Note: Quantities/seat limits are never changed by Reduce. If the resulting expiry is in the past, the license will be marked as expired.
                  </p>
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReduceModal(false)}>Cancel</Button>
            <Button 
              onClick={reduceLicense}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="confirm-reduce-btn"
            >
              <MinusCircle className="w-4 h-4 mr-2" /> Reduce Days
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Increase License Limit Modal (Feb 2026) */}
      <Dialog open={showIncreaseLimitModal} onOpenChange={setShowIncreaseLimitModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Increase License Limit</DialogTitle>
            <DialogDescription>
              Add more MT5 / NinjaTrader seats to this license. Expiry, product, status, and existing account bindings will NOT change.
            </DialogDescription>
          </DialogHeader>
          {selectedLicense && (() => {
            const lp = products.find((p) => p.product_id === selectedLicense.product_id);
            const supportsNt = !!lp?.ninjatrader_enabled;
            const curMt5 = typeof selectedLicense.mt5_max_activations === "number"
              ? selectedLicense.mt5_max_activations : 2;
            const curNt = typeof selectedLicense.nt_max_activations === "number"
              ? selectedLicense.nt_max_activations : 0;
            const curTot = curMt5 + curNt;
            const addMt5 = Math.max(0, parseInt(addMt5Qty) || 0);
            const addNt = supportsNt ? Math.max(0, parseInt(addNtQty) || 0) : 0;
            const newMt5 = curMt5 + addMt5;
            const newNt = curNt + addNt;
            const newTot = newMt5 + newNt;
            const canApply = addMt5 > 0 || addNt > 0;
            return (
              <div className="space-y-4 py-4">
                <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Key</span>
                    <code className="font-mono text-xs">{selectedLicense.license_key}</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Email</span>
                    <span>{selectedLicense.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Expiry (unchanged)</span>
                    <span>{new Date(selectedLicense.expiry_date).toLocaleDateString('en-IN')}</span>
                  </div>
                </div>

                {/* Current limits */}
                <div className="rounded-md border border-green-100 bg-green-50/40 p-3 space-y-1 text-sm">
                  <div className="text-xs font-semibold uppercase tracking-wide text-green-700 mb-1">
                    Current Limits
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">MT5</span>
                    <span data-testid="increase-current-mt5">{curMt5}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">NinjaTrader</span>
                    <span data-testid="increase-current-nt">{curNt}</span>
                  </div>
                  <div className="flex justify-between border-t border-green-100 pt-1">
                    <span className="text-gray-800 font-medium">Total</span>
                    <span className="font-semibold text-green-700" data-testid="increase-current-total">{curTot}</span>
                  </div>
                </div>

                {/* Additional seats */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="add-mt5-qty">Additional MT5 Access</Label>
                    <Input
                      id="add-mt5-qty"
                      type="number"
                      min={0}
                      value={addMt5Qty}
                      onChange={(e) => setAddMt5Qty(Math.max(0, parseInt(e.target.value) || 0))}
                      className="h-9 w-20 text-center"
                      data-testid="admin-increase-mt5-qty"
                    />
                  </div>
                  {supportsNt ? (
                    <div className="flex items-center justify-between">
                      <Label htmlFor="add-nt-qty">Additional NinjaTrader Access</Label>
                      <Input
                        id="add-nt-qty"
                        type="number"
                        min={0}
                        value={addNtQty}
                        onChange={(e) => setAddNtQty(Math.max(0, parseInt(e.target.value) || 0))}
                        className="h-9 w-20 text-center"
                        data-testid="admin-increase-nt-qty"
                      />
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500" data-testid="increase-no-nt-hint">
                      This product doesn&apos;t support NinjaTrader — NT seats can&apos;t be added.
                    </p>
                  )}
                </div>

                {/* New total preview */}
                <div className="rounded-md border border-indigo-100 bg-indigo-50/40 p-3 space-y-1 text-sm">
                  <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700 mb-1">
                    After Increase
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">MT5</span>
                    <span data-testid="increase-new-mt5">{curMt5} → <strong>{newMt5}</strong></span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">NinjaTrader</span>
                    <span data-testid="increase-new-nt">{curNt} → <strong>{newNt}</strong></span>
                  </div>
                  <div className="flex justify-between border-t border-indigo-100 pt-1">
                    <span className="text-gray-800 font-medium">Total</span>
                    <span className="font-semibold text-indigo-700" data-testid="increase-new-total">
                      {curTot} → <strong>{newTot}</strong>
                    </span>
                  </div>
                </div>

                {!canApply && (
                  <p className="text-xs text-red-600">Add at least one MT5 or NinjaTrader seat.</p>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIncreaseLimitModal(false)}>Cancel</Button>
            <Button
              onClick={increaseLicenseLimit}
              disabled={increaseLimitLoading || ((parseInt(addMt5Qty) || 0) === 0 && (parseInt(addNtQty) || 0) === 0)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="confirm-increase-limit-btn"
            >
              <TrendingUp className="w-4 h-4 mr-2" /> Apply Increase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decrease License Limit Modal (Feb 2026) */}
      <Dialog open={showDecreaseLimitModal} onOpenChange={setShowDecreaseLimitModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Decrease License Limit</DialogTitle>
            <DialogDescription>
              Remove MT5 / NinjaTrader seats from this license. Expiry, product, status, and existing account bindings will NOT change. New limit can&apos;t go below already-registered accounts/devices.
            </DialogDescription>
          </DialogHeader>
          {selectedLicense && (() => {
            const lp = products.find((p) => p.product_id === selectedLicense.product_id);
            const supportsNt = !!lp?.ninjatrader_enabled;
            const curMt5 = typeof selectedLicense.mt5_max_activations === "number"
              ? selectedLicense.mt5_max_activations : 2;
            const curNt = typeof selectedLicense.nt_max_activations === "number"
              ? selectedLicense.nt_max_activations : 0;
            const curTot = curMt5 + curNt;
            const registeredMt5 = (selectedLicense.registered_accounts || []).length;
            const registeredNt = (selectedLicense.nt_activations || []).length;
            const remMt5 = Math.max(0, parseInt(removeMt5Qty) || 0);
            const remNt = supportsNt ? Math.max(0, parseInt(removeNtQty) || 0) : 0;
            const newMt5 = curMt5 - remMt5;
            const newNt = curNt - remNt;
            const newTot = newMt5 + newNt;
            const belowRegisteredMt5 = newMt5 < registeredMt5;
            const belowRegisteredNt = newNt < registeredNt;
            const belowZero = newMt5 < 0 || newNt < 0;
            const noChange = remMt5 === 0 && remNt === 0;
            const canApply = !noChange && !belowRegisteredMt5 && !belowRegisteredNt && !belowZero;
            return (
              <div className="space-y-4 py-4">
                <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Key</span>
                    <code className="font-mono text-xs">{selectedLicense.license_key}</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Email</span>
                    <span>{selectedLicense.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Expiry (unchanged)</span>
                    <span>{new Date(selectedLicense.expiry_date).toLocaleDateString('en-IN')}</span>
                  </div>
                </div>

                {/* Current limits + registered counts */}
                <div className="rounded-md border border-orange-100 bg-orange-50/40 p-3 space-y-1 text-sm">
                  <div className="text-xs font-semibold uppercase tracking-wide text-orange-700 mb-1">
                    Current Limits
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">MT5</span>
                    <span data-testid="decrease-current-mt5">
                      {curMt5}
                      <span className="ml-2 text-xs text-gray-500">({registeredMt5} registered)</span>
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">NinjaTrader</span>
                    <span data-testid="decrease-current-nt">
                      {curNt}
                      <span className="ml-2 text-xs text-gray-500">({registeredNt} registered)</span>
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-orange-100 pt-1">
                    <span className="text-gray-800 font-medium">Total</span>
                    <span className="font-semibold text-orange-700" data-testid="decrease-current-total">{curTot}</span>
                  </div>
                </div>

                {/* Seats to remove */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="rem-mt5-qty">Remove MT5 Access</Label>
                    <Input
                      id="rem-mt5-qty"
                      type="number"
                      min={0}
                      value={removeMt5Qty}
                      onChange={(e) => setRemoveMt5Qty(Math.max(0, parseInt(e.target.value) || 0))}
                      className="h-9 w-20 text-center"
                      data-testid="admin-decrease-mt5-qty"
                    />
                  </div>
                  {supportsNt ? (
                    <div className="flex items-center justify-between">
                      <Label htmlFor="rem-nt-qty">Remove NinjaTrader Access</Label>
                      <Input
                        id="rem-nt-qty"
                        type="number"
                        min={0}
                        value={removeNtQty}
                        onChange={(e) => setRemoveNtQty(Math.max(0, parseInt(e.target.value) || 0))}
                        className="h-9 w-20 text-center"
                        data-testid="admin-decrease-nt-qty"
                      />
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500" data-testid="decrease-no-nt-hint">
                      This product doesn&apos;t support NinjaTrader — NT seats can&apos;t be removed.
                    </p>
                  )}
                </div>

                {/* Preview */}
                <div className="rounded-md border border-red-100 bg-red-50/40 p-3 space-y-1 text-sm">
                  <div className="text-xs font-semibold uppercase tracking-wide text-red-700 mb-1">
                    After Decrease
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">MT5</span>
                    <span data-testid="decrease-new-mt5">{curMt5} → <strong>{newMt5}</strong></span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">NinjaTrader</span>
                    <span data-testid="decrease-new-nt">{curNt} → <strong>{newNt}</strong></span>
                  </div>
                  <div className="flex justify-between border-t border-red-100 pt-1">
                    <span className="text-gray-800 font-medium">Total</span>
                    <span className="font-semibold text-red-700" data-testid="decrease-new-total">
                      {curTot} → <strong>{newTot}</strong>
                    </span>
                  </div>
                </div>

                {belowZero && (
                  <p className="text-xs text-red-600">New limit can&apos;t go below 0.</p>
                )}
                {belowRegisteredMt5 && !belowZero && (
                  <p className="text-xs text-red-600" data-testid="decrease-below-registered-mt5">
                    New MT5 ({newMt5}) is below {registeredMt5} already-registered accounts. Disconnect accounts first.
                  </p>
                )}
                {belowRegisteredNt && !belowZero && (
                  <p className="text-xs text-red-600" data-testid="decrease-below-registered-nt">
                    New NT ({newNt}) is below {registeredNt} already-registered devices. Disconnect devices first.
                  </p>
                )}
                {noChange && (
                  <p className="text-xs text-red-600">Remove at least one MT5 or NinjaTrader seat.</p>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDecreaseLimitModal(false)}>Cancel</Button>
            <Button
              onClick={decreaseLicenseLimit}
              disabled={decreaseLimitLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-decrease-limit-btn"
            >
              <MinusCircle className="w-4 h-4 mr-2" /> Apply Decrease
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Accounts Modal */}
      <Dialog open={showAccountsModal} onOpenChange={setShowAccountsModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Monitor className="w-5 h-5" />
              Registered Accounts
            </DialogTitle>
            <DialogDescription>
              Trading accounts registered to this license (max {selectedLicense?.mt5_max_activations || 2})
            </DialogDescription>
          </DialogHeader>
          {selectedLicense && (
            <div className="space-y-4 py-4" data-testid="admin-accounts-scroll">
              <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm">
                <div className="flex justify-between mb-1">
                  <span className="text-gray-500">License Key</span>
                  <code className="font-mono text-xs">{selectedLicense.license_key}</code>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Email</span>
                  <span>{selectedLicense.email}</span>
                </div>
                {(selectedLicense.customer_name || selectedLicense.customer_phone) && (
                  <>
                    {selectedLicense.customer_name && (
                      <div className="flex justify-between mt-1">
                        <span className="text-gray-500">Customer Name</span>
                        <span data-testid="license-customer-name">{selectedLicense.customer_name}</span>
                      </div>
                    )}
                    {selectedLicense.customer_phone && (
                      <div className="flex justify-between mt-1">
                        <span className="text-gray-500">Customer Phone</span>
                        <span data-testid="license-customer-phone">{selectedLicense.customer_phone}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Purchased Entitlements — visible only when this license
                  carries the Razorpay-notes-v2 quantity fields. Legacy
                  licenses skip this block entirely so nothing changes for them. */}
              {(selectedLicense.mt5_max_activations || selectedLicense.nt_max_activations || selectedLicense.total_purchased_activations) && (
                <div
                  className="bg-indigo-50 border border-indigo-200 rounded-md p-3 text-sm"
                  data-testid="license-entitlements-panel"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Layers className="w-4 h-4 text-indigo-700" />
                    <span className="font-semibold text-indigo-900 uppercase tracking-wider text-xs">
                      Purchased Entitlements
                    </span>
                  </div>
                  {selectedLicense.total_purchased_activations != null && (
                    <div className="flex justify-between mb-1">
                      <span className="text-indigo-800">Total Activations</span>
                      <span className="font-semibold" data-testid="license-total-activations">
                        {selectedLicense.total_purchased_activations}
                      </span>
                    </div>
                  )}
                  {selectedLicense.mt5_max_activations != null && (
                    <div className="flex justify-between mb-1">
                      <span className="text-indigo-800">MT5</span>
                      <span className="font-mono" data-testid="license-mt5-usage">
                        {(selectedLicense.registered_accounts || []).length} / {selectedLicense.mt5_max_activations} used
                      </span>
                    </div>
                  )}
                  {selectedLicense.nt_max_activations != null && (
                    <div className="flex justify-between">
                      <span className="text-indigo-800">NinjaTrader</span>
                      <span className="font-mono" data-testid="license-nt-usage">
                        {(selectedLicense.nt_activations || []).length} / {selectedLicense.nt_max_activations} used
                      </span>
                    </div>
                  )}
                </div>
              )}

              {selectedLicense.registered_accounts && selectedLicense.registered_accounts.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">
                      Active Accounts ({selectedLicense.registered_accounts.length}/{selectedLicense.mt5_max_activations || 2})
                    </Label>
                    <Badge variant="outline" className={selectedLicense.registered_accounts.length >= (selectedLicense.mt5_max_activations || 2) ? "bg-red-50 text-red-700 border-red-200" : "bg-green-50 text-green-700 border-green-200"}>
                      {selectedLicense.registered_accounts.length >= (selectedLicense.mt5_max_activations || 2) ? "Max Reached" : `${(selectedLicense.mt5_max_activations || 2) - selectedLicense.registered_accounts.length} Slot Available`}
                    </Badge>
                  </div>
                  {selectedLicense.registered_accounts.map((acc, index) => {
                    const account = typeof acc === 'object' ? acc : { account: acc, broker: 'Unknown' };
                    return (
                      <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-indigo-200 transition-colors">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-mono text-lg font-bold text-gray-900 truncate">{account.account}</span>
                            <button 
                              onClick={() => copyToClipboard(account.account)}
                              className="text-gray-400 hover:text-indigo-600 flex-shrink-0"
                              title="Copy account number"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Badge className="bg-green-100 text-green-800 border-green-200">Active</Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={disconnectLoading === account.account}
                              onClick={() => disconnectAccount(selectedLicense, account.account)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                              data-testid={`admin-disconnect-account-btn-${account.account}`}
                              title="Disconnect this account"
                            >
                              <Unplug className="w-4 h-4 sm:mr-1.5" />
                              <span className="hidden sm:inline text-xs">
                                {disconnectLoading === account.account ? "..." : "Disconnect"}
                              </span>
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="bg-gray-50 rounded p-2">
                            <span className="text-gray-500 text-xs uppercase">Broker</span>
                            <p className="text-gray-900 font-medium">{account.broker || 'Unknown'}</p>
                          </div>
                          {account.registered_at && (
                            <div className="bg-gray-50 rounded p-2">
                              <span className="text-gray-500 text-xs uppercase">Registered</span>
                              <p className="text-gray-700">
                                {new Date(account.registered_at).toLocaleDateString('en-IN')}
                              </p>
                            </div>
                          )}
                          {account.last_used && (
                            <div className="col-span-2 bg-gray-50 rounded p-2">
                              <span className="text-gray-500 text-xs uppercase">Last Validated</span>
                              <p className="text-gray-700">
                                {new Date(account.last_used).toLocaleString('en-IN')}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Monitor className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No accounts registered yet</p>
                  <p className="text-sm">Accounts will appear here when the EA validates</p>
                  <p className="text-xs mt-2 text-gray-400">
                    Max {selectedLicense.mt5_max_activations || 2} account
                    {(selectedLicense.mt5_max_activations || 2) > 1 ? "s" : ""} per license
                  </p>
                </div>
              )}

              {/* NinjaTrader activations — one card per account_number so
                  admins can disconnect a specific account even when
                  multiple accounts share the same machine (device_id). */}
              {selectedLicense.nt_activations && selectedLicense.nt_activations.length > 0 && (
                <div className="pt-3 mt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Package className="w-4 h-4 text-orange-600" />
                      NinjaTrader Accounts ({selectedLicense.nt_activations.length}
                      {selectedLicense.nt_max_activations ? `/${selectedLicense.nt_max_activations}` : ""})
                    </Label>
                  </div>
                  <div className="space-y-2">
                    {selectedLicense.nt_activations.map((d, index) => {
                      const acct = String(d.account_number || "").trim();
                      const key = acct || `nt-${d.device_id || index}`;
                      const isLegacy = !acct;
                      return (
                      <div key={key} className="bg-white border border-orange-100 rounded-lg p-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            {acct ? (
                              <p className="font-mono text-xs font-semibold text-gray-900 truncate" title={acct}>
                                Account: {acct}
                              </p>
                            ) : (
                              <p className="font-mono text-xs font-semibold text-amber-700 truncate" title="Legacy device-only entry">
                                Legacy device (no account_number)
                              </p>
                            )}
                            <p className="text-xs text-gray-500 truncate font-mono" title={d.device_id}>
                              Device: {d.device_id || "unknown"}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {d.machine_name || "Unknown machine"}
                            </p>
                            {d.last_heartbeat && (
                              <p className="text-xs text-gray-400 mt-1">
                                Last heartbeat: {new Date(d.last_heartbeat).toLocaleString("en-IN")}
                              </p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={disconnectLoading === `nt-${acct || d.device_id}`}
                            onClick={() => disconnectNtDevice(selectedLicense, d)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                            data-testid={`admin-disconnect-nt-btn-${acct || d.device_id}`}
                            title={isLegacy ? "Disconnect this legacy device" : `Disconnect account ${acct}`}
                          >
                            <Unplug className="w-4 h-4 sm:mr-1.5" />
                            <span className="hidden sm:inline text-xs">
                              {disconnectLoading === `nt-${acct || d.device_id}` ? "..." : "Disconnect"}
                            </span>
                          </Button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAccountsModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials Modal */}
      <Dialog open={showCredentialsModal} onOpenChange={setShowCredentialsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-indigo-600" />
              Client Credentials
            </DialogTitle>
            <DialogDescription>
              Login credentials for {selectedLicense?.email}
            </DialogDescription>
          </DialogHeader>
          {selectedLicense && (
            <div className="space-y-4 py-4">
              {/* Email */}
              <div className="space-y-2">
                <Label className="text-xs text-gray-500 uppercase tracking-wider">Email</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm font-mono bg-gray-50 px-3 py-2 rounded border">
                    {selectedLicense.email}
                  </code>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => copyToClipboard(selectedLicense.email)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label className="text-xs text-gray-500 uppercase tracking-wider">Password</Label>
                {selectedLicense.client_password ? (
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-sm font-mono bg-gray-50 px-3 py-2 rounded border text-green-700 font-semibold">
                      {selectedLicense.client_password}
                    </code>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => copyToClipboard(selectedLicense.client_password)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-800">
                      Password not available for this license. This applies only to legacy licenses created before the credentials-storage fix. Newly created or manually created licenses always include a viewable password here.
                    </p>
                  </div>
                )}
              </div>

              {/* License Key */}
              <div className="space-y-2">
                <Label className="text-xs text-gray-500 uppercase tracking-wider">License Key</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm font-mono bg-gray-50 px-3 py-2 rounded border break-all">
                    {selectedLicense.license_key}
                  </code>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => copyToClipboard(selectedLicense.license_key)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Copy All Button */}
              {selectedLicense.client_password && (
                <Button 
                  className="w-full"
                  onClick={() => {
                    const text = `Email: ${selectedLicense.email}\nPassword: ${selectedLicense.client_password}\nLicense Key: ${selectedLicense.license_key}`;
                    copyToClipboard(text);
                  }}
                >
                  <Copy className="w-4 h-4 mr-2" /> Copy All Credentials
                </Button>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCredentialsModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete License Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete License
            </DialogTitle>
            <DialogDescription className="pt-2">
              Are you sure you want to permanently delete this license? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {selectedLicense && (
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Email:</span>
                  <span className="font-medium text-gray-900">{selectedLicense.email}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">License Key:</span>
                  <span className="font-mono text-xs text-gray-900">{selectedLicense.license_key?.substring(0, 12)}...</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Status:</span>
                  <Badge className={
                    selectedLicense.status === "active" ? "bg-green-100 text-green-800" :
                    selectedLicense.status === "expired" ? "bg-red-100 text-red-800" :
                    "bg-gray-100 text-gray-800"
                  }>
                    {selectedLicense.status?.toUpperCase()}
                  </Badge>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteModal(false)}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={deleteLicense}
              disabled={deleteLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Permanently
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EA Download Link Config Modal (Google Drive) */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-green-600" />
              EA Download Link
            </DialogTitle>
            <DialogDescription>
              Paste the Google Drive shareable link for the compiled <code className="font-mono">.ex5</code> file. Clients receive this link after their active-license check.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Current settings */}
            {currentEaInfo && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
                <h4 className="text-sm font-medium text-gray-700">Current Settings</h4>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-500 shrink-0">Filename:</span>
                    <span className="font-mono text-xs truncate" title={currentEaInfo.filename}>
                      {currentEaInfo.filename}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Version:</span>
                    <Badge variant="outline">{currentEaInfo.version}</Badge>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-500 shrink-0">Drive URL:</span>
                    <span className="font-mono text-xs truncate max-w-[280px]" title={currentEaInfo.download_url}>
                      {currentEaInfo.download_url || <em className="text-red-500 not-italic">not set</em>}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Form */}
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="ea-drive-url">Google Drive URL <span className="text-red-500">*</span></Label>
                <Input
                  id="ea-drive-url"
                  placeholder="https://drive.google.com/file/d/FILE_ID/view?usp=sharing"
                  value={uploadDriveUrl}
                  onChange={(e) => setUploadDriveUrl(e.target.value)}
                  data-testid="admin-ea-drive-url-input"
                />
                <p className="text-xs text-gray-500">
                  Paste the &quot;Anyone with the link&quot; share URL. It will be auto-converted to a direct download link. Make sure sharing is set to <b>Anyone with the link — Viewer</b> in Google Drive.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="ea-version">Version</Label>
                  <Input
                    id="ea-version"
                    placeholder="e.g., 3.2"
                    value={uploadVersion}
                    onChange={(e) => setUploadVersion(e.target.value)}
                    data-testid="admin-ea-version-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ea-filename">Filename</Label>
                  <Input
                    id="ea-filename"
                    placeholder="MonkAlgo_BreakoutEA_V3.2.ex5"
                    value={uploadFilename}
                    onChange={(e) => setUploadFilename(e.target.value)}
                    data-testid="admin-ea-filename-input"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Version and Filename are shown to clients on the portal. The file itself lives on Google Drive.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUploadEA}
              disabled={uploadLoading || !uploadDriveUrl.trim()}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="admin-ea-save-btn"
            >
              {uploadLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Save Link
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Product (one-time migration tool for mis-tagged legacy licenses) */}
      <Dialog open={showReassignModal} onOpenChange={setShowReassignModal}>
        <DialogContent className="sm:max-w-md" data-testid="reassign-product-modal">
          <DialogHeader>
            <DialogTitle>Change Product for this License</DialogTitle>
            <DialogDescription>
              Retag this license to a different product without touching its
              expiry, accounts, or subscription. Useful for correcting licenses
              that were created before multi-product support was fully enabled.
            </DialogDescription>
          </DialogHeader>
          {reassignTarget && (
            <div className="space-y-3 py-3">
              <div className="text-xs text-gray-500">
                Customer: <b className="text-gray-800">{reassignTarget.email}</b><br />
                License key: <code className="font-mono text-[11px] bg-gray-100 px-1.5 py-0.5 rounded">{reassignTarget.license_key}</code><br />
                Currently tagged: <b>{productMap[reassignTarget.product_id || "monk-breakout-ea"]?.name || (reassignTarget.product_id || "monk-breakout-ea")}</b>
              </div>
              <div className="space-y-2">
                <Label htmlFor="reassign-product">New product</Label>
                <Select value={reassignToProductId} onValueChange={setReassignToProductId}>
                  <SelectTrigger id="reassign-product" data-testid="reassign-product-select">
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((p) => (
                      <SelectItem key={p.product_id} value={p.product_id}>
                        {p.name} <span className="text-gray-400 text-xs">({p.product_id})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReassignModal(false)} disabled={reassignSaving}>
              Cancel
            </Button>
            <Button
              onClick={submitReassign}
              disabled={reassignSaving}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="reassign-product-confirm"
            >
              {reassignSaving ? "Saving..." : "Retag License"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Create / Edit Modal */}
      <Dialog open={showProductModal} onOpenChange={(o) => { setShowProductModal(o); if (!o) setProductForm(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{productModalMode === "create" ? "Add Product" : "Edit Product"}</DialogTitle>
            <DialogDescription>
              Master configuration for a product. All fields drive the Client Portal, webhook amount validation and downloads.
            </DialogDescription>
          </DialogHeader>
          {productForm && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="p-name">Product Name</Label>
                  <Input
                    id="p-name"
                    placeholder="Monk Breakout EA"
                    value={productForm.name}
                    onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                    data-testid="product-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="p-slug">
                    Product Slug{" "}
                    <span className="text-xs text-gray-500">(unique, immutable)</span>
                  </Label>
                  <Input
                    id="p-slug"
                    placeholder="monk-breakout-ea"
                    value={productForm.product_id}
                    onChange={(e) => setProductForm({ ...productForm, product_id: e.target.value.toLowerCase() })}
                    disabled={productModalMode === "edit"}
                    data-testid="product-slug-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="p-group">
                    Display Group{" "}
                    <span className="text-xs text-gray-500">(groups variants under one client card)</span>
                  </Label>
                  <Input
                    id="p-group"
                    placeholder="monk-algo-orb"
                    value={productForm.display_group}
                    onChange={(e) => setProductForm({ ...productForm, display_group: e.target.value.toLowerCase() })}
                    data-testid="product-group-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="p-cycle">Billing Cycle</Label>
                  <Select
                    value={productForm.billing_cycle}
                    onValueChange={(v) => setProductForm({ ...productForm, billing_cycle: v })}
                  >
                    <SelectTrigger id="p-cycle" data-testid="product-cycle-select">
                      <SelectValue placeholder="monthly" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly (30d)</SelectItem>
                      <SelectItem value="quarterly">Quarterly (90d)</SelectItem>
                      <SelectItem value="yearly">Yearly (365d)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="p-price">Price (paise)</Label>
                  <Input
                    id="p-price"
                    type="number"
                    min={1}
                    value={productForm.price_paise}
                    onChange={(e) => setProductForm({ ...productForm, price_paise: parseInt(e.target.value) || 0 })}
                    data-testid="product-price-input"
                  />
                  <p className="text-xs text-gray-500">
                    ₹{((productForm.price_paise || 0) / 100).toLocaleString("en-IN")} — enter in paise (1 INR = 100 paise)
                  </p>
                  {productForm.display_group === "monk-algo-orb" && (
                    <p
                      className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded p-2 leading-snug"
                      data-testid="product-price-orb-hint"
                    >
                      <b>ORB tiered pricing:</b> this is the base price for <b>2 total activations</b>.
                      Per-seat = ½ of this. E.g. ₹29,997 → 3 seats = ₹44,995.50, 4 seats = ₹59,994.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="p-version">Version</Label>
                  <Input
                    id="p-version"
                    placeholder="3.0"
                    value={productForm.version}
                    onChange={(e) => setProductForm({ ...productForm, version: e.target.value })}
                    data-testid="product-version-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="p-drive">Google Drive Download URL</Label>
                <Input
                  id="p-drive"
                  placeholder="https://drive.google.com/file/d/…/view"
                  value={productForm.drive_url}
                  onChange={(e) => setProductForm({ ...productForm, drive_url: e.target.value })}
                  data-testid="product-drive-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="p-rzp">Razorpay Payment / Subscription Link</Label>
                <Input
                  id="p-rzp"
                  placeholder="https://rzp.io/rzp/xxxxxxxx"
                  value={productForm.razorpay_link}
                  onChange={(e) => setProductForm({ ...productForm, razorpay_link: e.target.value })}
                  data-testid="product-razorpay-input"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="flex items-center gap-2 p-3 border border-gray-200 rounded-md cursor-pointer">
                  <input
                    type="checkbox"
                    checked={productForm.tv_enabled}
                    onChange={(e) => setProductForm({ ...productForm, tv_enabled: e.target.checked })}
                    data-testid="product-tv-toggle"
                  />
                  <span className="text-sm">TradingView Enabled</span>
                </label>
                <label className="flex items-center gap-2 p-3 border border-gray-200 rounded-md cursor-pointer">
                  <input
                    type="checkbox"
                    checked={productForm.license_enabled}
                    onChange={(e) => setProductForm({ ...productForm, license_enabled: e.target.checked })}
                    data-testid="product-mt5-toggle"
                  />
                  <span className="text-sm">MT5 Validation Enabled</span>
                </label>
                <label className="flex items-center gap-2 p-3 border border-gray-200 rounded-md cursor-pointer">
                  <input
                    type="checkbox"
                    checked={productForm.active}
                    onChange={(e) => setProductForm({ ...productForm, active: e.target.checked })}
                    data-testid="product-active-toggle"
                  />
                  <span className="text-sm">Active (visible to clients)</span>
                </label>
              </div>

              {/* Branding assets (all optional) */}
              <div className="pt-2 border-t border-gray-100">
                <div className="flex items-center gap-2 mb-2">
                  <ImageIcon className="w-4 h-4 text-gray-500" />
                  <Label className="text-sm font-semibold text-gray-700">Branding</Label>
                  <span className="text-xs text-gray-400">(all optional — Client Portal shows fallback icon if empty)</span>
                </div>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label htmlFor="p-logo" className="text-xs">Logo URL <span className="text-gray-400">(square, shown on client product card)</span></Label>
                    <div className="flex gap-2">
                      <Input
                        id="p-logo"
                        placeholder="https://…/monk-breakout-logo.png"
                        value={productForm.logo_url}
                        onChange={(e) => setProductForm({ ...productForm, logo_url: e.target.value })}
                        data-testid="product-logo-input"
                      />
                      {productForm.logo_url && (
                        <img src={productForm.logo_url} alt="logo" className="w-10 h-10 rounded border border-gray-200 object-cover" onError={(e) => e.currentTarget.style.opacity = "0.3"} />
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="p-image" className="text-xs">Product Image URL <span className="text-gray-400">(used as logo fallback)</span></Label>
                    <Input
                      id="p-image"
                      placeholder="https://…/monk-breakout-image.png"
                      value={productForm.image_url}
                      onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })}
                      data-testid="product-image-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="p-banner" className="text-xs">Banner URL <span className="text-gray-400">(wide, shown across the top of the client card)</span></Label>
                    <Input
                      id="p-banner"
                      placeholder="https://…/monk-breakout-banner.jpg"
                      value={productForm.banner_url}
                      onChange={(e) => setProductForm({ ...productForm, banner_url: e.target.value })}
                      data-testid="product-banner-input"
                    />
                    {productForm.banner_url && (
                      <div
                        className="h-20 rounded border border-gray-200 bg-cover bg-center"
                        style={{ backgroundImage: `url(${productForm.banner_url})` }}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* NinjaTrader 8 entitlement (only permitted on 'monk-algo-orb') */}
              <div className="pt-2 border-t border-gray-100">
                <div className="flex items-center gap-2 mb-2">
                  <Package className="w-4 h-4 text-orange-600" />
                  <Label className="text-sm font-semibold text-gray-700">NinjaTrader 8</Label>
                  <span className="text-xs text-gray-400">
                    (available only on <code className="bg-gray-100 px-1 rounded">monk-algo-orb</code> display group)
                  </span>
                </div>
                {productForm.display_group !== "monk-algo-orb" && productForm.ninjatrader_enabled && (
                  <p className="text-xs text-red-600 mb-2" data-testid="product-nt-eligibility-warning">
                    Change the Display Group to <b>monk-algo-orb</b> before enabling NinjaTrader — the server will reject the save otherwise.
                  </p>
                )}
                <label className={`flex items-center gap-2 p-3 border rounded-md cursor-pointer mb-3 ${
                  productForm.display_group === "monk-algo-orb"
                    ? "border-orange-200 bg-orange-50/40"
                    : "border-gray-200 bg-gray-50 opacity-60"
                }`}>
                  <input
                    type="checkbox"
                    checked={productForm.ninjatrader_enabled}
                    disabled={productForm.display_group !== "monk-algo-orb"}
                    onChange={(e) => setProductForm({ ...productForm, ninjatrader_enabled: e.target.checked })}
                    data-testid="product-nt-toggle"
                  />
                  <span className="text-sm">NinjaTrader Access Enabled</span>
                </label>
                {productForm.ninjatrader_enabled && (
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label htmlFor="p-nt-drive" className="text-xs">
                        NinjaTrader Download URL <span className="text-gray-400">(Google Drive share link for the .zip/strategy file)</span>
                      </Label>
                      <Input
                        id="p-nt-drive"
                        placeholder="https://drive.google.com/file/d/…/view"
                        value={productForm.nt_drive_url}
                        onChange={(e) => setProductForm({ ...productForm, nt_drive_url: e.target.value })}
                        data-testid="product-nt-drive-input"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label htmlFor="p-nt-devices" className="text-xs">
                          Max NT Devices per License <span className="text-gray-400 font-normal">(legacy fallback only)</span>
                        </Label>
                        <Input
                          id="p-nt-devices"
                          type="number"
                          min={1}
                          value={productForm.nt_max_devices}
                          onChange={(e) => setProductForm({ ...productForm, nt_max_devices: parseInt(e.target.value) || 2 })}
                          data-testid="product-nt-max-devices-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Used only for legacy licenses that were created before per-customer quantities were introduced. New paid purchases override this with the Razorpay <code className="bg-gray-100 px-1 rounded">ninjatrader_quantity</code> stored on each license as <code className="bg-gray-100 px-1 rounded">nt_max_activations</code>.
                        </p>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="p-nt-version" className="text-xs">NT Version</Label>
                        <Input
                          id="p-nt-version"
                          placeholder="1.0"
                          value={productForm.nt_version}
                          onChange={(e) => setProductForm({ ...productForm, nt_version: e.target.value })}
                          data-testid="product-nt-version-input"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProductModal(false)} disabled={productSaving}>
              Cancel
            </Button>
            <Button
              onClick={saveProduct}
              disabled={productSaving}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="save-product-btn"
            >
              {productSaving ? "Saving..." : productModalMode === "create" ? "Create Product" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Stats Modal */}
      <Dialog open={showStatsModal} onOpenChange={setShowStatsModal}>
        <DialogContent className="sm:max-w-lg" data-testid="product-stats-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-indigo-600" />
              {statsProduct?.name || "Product"} — Analytics
            </DialogTitle>
            <DialogDescription>
              Real-time stats scoped to <code className="text-xs bg-gray-100 px-1 rounded">{statsProduct?.product_id}</code>.
            </DialogDescription>
          </DialogHeader>
          {statsLoading ? (
            <div className="py-10 text-center text-gray-500">
              <RefreshCw className="w-6 h-6 mx-auto animate-spin mb-2" />
              Loading stats…
            </div>
          ) : productStats ? (
            <div className="grid grid-cols-2 gap-3 py-2">
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                <div className="text-xs uppercase text-gray-500 flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" /> Total Customers
                </div>
                <div className="text-xl font-bold text-gray-900 mt-1" data-testid="stat-total-customers">
                  {productStats.total_customers}
                </div>
              </div>
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="text-xs uppercase text-green-700 flex items-center gap-1">
                  <CheckCircle className="w-3.5 h-3.5" /> Active Licenses
                </div>
                <div className="text-xl font-bold text-green-900 mt-1" data-testid="stat-active-licenses">
                  {productStats.active_licenses}
                </div>
              </div>
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="text-xs uppercase text-red-700 flex items-center gap-1">
                  <XCircle className="w-3.5 h-3.5" /> Expired Licenses
                </div>
                <div className="text-xl font-bold text-red-900 mt-1" data-testid="stat-expired-licenses">
                  {productStats.expired_licenses}
                </div>
              </div>
              <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                <div className="text-xs uppercase text-indigo-700 flex items-center gap-1">
                  <DollarSign className="w-3.5 h-3.5" /> Revenue
                </div>
                <div className="text-xl font-bold text-indigo-900 mt-1" data-testid="stat-revenue">
                  {productStats.revenue_inr}
                </div>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="text-xs uppercase text-yellow-700 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> Renewals Due (7d)
                </div>
                <div className="text-xl font-bold text-yellow-900 mt-1" data-testid="stat-renewals-due">
                  {productStats.renewals_due_7d}
                </div>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-xs uppercase text-blue-700 flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" /> Pending TV Requests
                </div>
                <div className="text-xl font-bold text-blue-900 mt-1" data-testid="stat-pending-tv">
                  {productStats.pending_tv_requests}
                </div>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500">No data available.</div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatsModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Extend / Reduce Modal */}
      <Dialog open={showBulkExtendModal} onOpenChange={setShowBulkExtendModal}>
        <DialogContent className="sm:max-w-md" data-testid="bulk-extend-modal">
          <DialogHeader>
            <DialogTitle>
              Bulk {bulkExtendMode === "extend" ? "Extend" : "Reduce"} — {selectedLicenseIds.length} license(s)
            </DialogTitle>
            <DialogDescription>
              {bulkExtendMode === "extend"
                ? "Adds days to each selected license and marks them active."
                : "Removes days from each selected license. Licenses whose expiry falls in the past will be marked expired."}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="bulk-days">Days</Label>
            <Input
              id="bulk-days"
              type="number"
              min={1}
              value={bulkExtendDays}
              onChange={(e) => setBulkExtendDays(parseInt(e.target.value) || 0)}
              data-testid="bulk-days-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkExtendModal(false)} disabled={bulkLoading}>
              Cancel
            </Button>
            <Button
              onClick={() => runBulkAction(bulkExtendMode, bulkExtendDays)}
              disabled={bulkLoading || bulkExtendDays <= 0}
              className={bulkExtendMode === "extend" ? "bg-green-600 hover:bg-green-700 text-white" : "bg-orange-600 hover:bg-orange-700 text-white"}
              data-testid="bulk-days-confirm"
            >
              {bulkLoading ? "Working..." : (bulkExtendMode === "extend" ? "Extend" : "Reduce")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
